import { ReactNode } from 'react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';

/*
 * TimeSlotGrid — DESIGN_SYSTEM.md §2.2
 *
 * Auto-fill grid (min 96px tracks). Each slot is a two-line button (time +
 * pro/sublabel). 1.5px border thickening on hover/select. Taken slots:
 * opacity 0.4 + strikethrough.
 */

export interface TimeSlot {
  /** Slot start time. */
  start: Date;
  /** Optional secondary line (provider name, room, etc). */
  sublabel?: string;
  /** Optional rich content below sublabel (e.g. capacity bar for group classes). */
  footer?: ReactNode;
  /** Disabled slot — strike + dim. */
  taken?: boolean;
  disabled?: boolean;
  /** Arbitrary identifier you'll get back in onSelect. */
  id?: string;
}

interface TimeSlotGridProps {
  slots: TimeSlot[];
  selectedId?: string;
  onSelect?: (slot: TimeSlot) => void;
  /** Empty-state action (e.g. "Try another time" outline button). */
  onEmptyAction?: () => void;
  emptyActionLabel?: ReactNode;
  emptyTitle?: ReactNode;
  /**
   * 'grid' (default) = compact centered tiles. 'list' = single column, info
   * aligned on one row. 'cards' = grid of left-aligned tiles with room for the
   * sublabel and footer (group times on wider screens).
   */
  layout?: 'grid' | 'list' | 'cards';
  /** Fixed grid columns (e.g. 'grid-cols-3 sm:grid-cols-4') instead of auto-fill. */
  columnsClassName?: string;
  /** 'lg' = 52px tiles with a 15px time, used on the public booking steps. */
  size?: 'md' | 'lg';
  className?: string;
}

export function TimeSlotGrid({
  slots,
  selectedId,
  onSelect,
  onEmptyAction,
  emptyActionLabel = 'Try another time',
  emptyTitle = 'No times available',
  layout = 'grid',
  columnsClassName,
  size = 'md',
  className,
}: TimeSlotGridProps) {
  if (slots.length === 0) {
    return (
      <EmptyState
        title={emptyTitle}
        description="Try a different day or change the service."
        action={
          onEmptyAction && (
            <Button variant="outline-brand" onClick={onEmptyAction}>
              {emptyActionLabel}
            </Button>
          )
        }
        className={className}
      />
    );
  }

  const isList = layout === 'list';
  const isCards = layout === 'cards';
  const large = size === 'lg';

  return (
    <div
      className={cn('grid gap-2', isList ? 'grid-cols-1' : columnsClassName, className)}
      style={isList || columnsClassName ? undefined : { gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))' }}
      data-layout={layout}
    >
      {slots.map((slot, i) => {
        const id = slot.id ?? slot.start.toISOString();
        const selected = selectedId === id;
        return (
          <button
            key={id + i}
            type="button"
            disabled={slot.taken || slot.disabled}
            onClick={() => onSelect?.(slot)}
            className={cn(
              'rounded-lg border-[1.5px] bg-surface transition-colors',
              large && !isList && !isCards && 'min-h-[52px]',
              large && isList && 'flex-wrap gap-x-3 gap-y-2.5 rounded-2xl px-4 py-3.5',
              'hover:border-brand-300 hover:bg-brand-50',
              'disabled:cursor-not-allowed disabled:opacity-40',
              isList && 'flex w-full items-center gap-3 px-3 py-2.5 text-left',
              isCards && 'flex min-w-0 flex-col items-start gap-1 rounded-2xl px-4 py-3.5 text-left',
              !isList && !isCards && 'flex flex-col items-center justify-center gap-0.5 px-2 py-2.5 text-center',
              slot.taken && 'line-through',
              selected
                ? 'border-brand-500 bg-brand-50'
                : 'border-line',
            )}
            aria-pressed={selected}
            data-testid="time-slot"
          >
            <span
              className={cn(
                'font-semibold tabular-nums text-ink',
                large ? 'text-[15px]' : 'text-body-sm',
                isList && 'shrink-0',
                selected && 'text-brand-700',
              )}
            >
              {format(slot.start, 'HH:mm')}
            </span>
            {slot.sublabel && (
              <span
                className={cn(
                  cn('truncate text-soft', large && (isList || isCards) ? 'text-[13px]' : 'text-[11px]'),
                  isList && 'min-w-0 flex-1',
                  isCards && 'w-full',
                )}
              >
                {slot.sublabel}
              </span>
            )}
            {slot.footer && (
              <div className={cn(isList ? (large ? 'w-full' : 'ml-auto shrink-0') : isCards ? 'mt-1.5 w-full' : 'mt-1 w-full')}>
                {slot.footer}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}
